/**
 * SettingsHubPage — `/settings`.
 *
 * Tile grid landing page that links into the 13 settings sub-screens.
 * Pure UI — recent-changes feed is a deferred surface (no audit endpoint
 * scoped to settings yet).
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  Building2,
  CreditCard,
  Landmark,
  Layers,
  type LucideIcon,
  Percent,
  PieChart,
  ShieldAlert,
  Sliders,
  Timer,
  Wallet,
} from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';

interface Tile {
  readonly title: string;
  readonly description: string;
  readonly to: string;
  readonly icon: LucideIcon;
  readonly tint: string;
}

const TILES: readonly Tile[] = [
  { title: 'Outlets', description: 'Manage NIPOST and franchise outlet configurations', to: '/settings/outlet-management', icon: Building2, tint: 'bg-green-50 text-green-700' },
  { title: 'Pricing', description: 'Configure service pricing by weight band and zone', to: '/settings/pricing-config', icon: CreditCard, tint: 'bg-blue-50 text-blue-700' },
  { title: 'Discounts', description: 'Volume, promotional and corporate discount rules', to: '/settings/discount-rules', icon: Percent, tint: 'bg-purple-50 text-purple-700' },
  { title: 'SLAs', description: 'Delivery and complaint resolution targets', to: '/settings/sla-config', icon: Timer, tint: 'bg-orange-50 text-orange-700' },
  { title: 'Transaction limits', description: 'Financial transaction limits by KYC and tier', to: '/settings/transaction-limits', icon: CreditCard, tint: 'bg-cyan-50 text-cyan-700' },
  { title: 'Fraud rules', description: 'Build and manage fraud detection rule conditions', to: '/settings/fraud-rules', icon: ShieldAlert, tint: 'bg-red-50 text-red-700' },
  { title: 'Government services', description: 'Configure MDA integrations, fees and eligibility', to: '/settings/gov-service-config', icon: Landmark, tint: 'bg-indigo-50 text-indigo-700' },
  { title: 'Tier matrix', description: 'Define which services are available at each tier', to: '/settings/tier-service-matrix', icon: Layers, tint: 'bg-teal-50 text-teal-700' },
  { title: 'Revenue sharing', description: 'Set revenue split rules across stakeholders', to: '/settings/revenue-sharing', icon: PieChart, tint: 'bg-amber-50 text-amber-700' },
  { title: 'Notification templates', description: 'Manage SMS, email and push templates', to: '/settings/notification-templates', icon: Bell, tint: 'bg-pink-50 text-pink-700' },
  { title: 'Recipient accounts', description: 'Settlement and remittance bank accounts', to: '/settings/recipient-accounts', icon: Wallet, tint: 'bg-violet-50 text-violet-700' },
  { title: 'System parameters', description: 'Tune platform-wide configuration parameters', to: '/settings/system-parameters', icon: Sliders, tint: 'bg-gray-50 text-gray-700' },
];

export function SettingsHubPage(): JSX.Element {
  return (
    <div className="space-y-6">
      <PageHeader
        title="System settings"
        description="Central configuration hub for the UPMP platform."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <Link
              key={tile.title}
              to={tile.to}
              className="bg-white rounded-xl border border-border p-5 hover:shadow-md hover:border-nipost-green/30 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tile.tint}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 group-hover:text-nipost-green transition-colors">
                    {tile.title}
                  </h3>
                  <p className="text-xs text-muted mt-1">{tile.description}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
